import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { uploadToCloudinary } from '../lib/cloudinary'
import { useAuth } from '../contexts/AuthContext'
import './AdminSettings.css'

export default function AdminSettings() {
  const { user, profile, fetchProfile, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const avatarRef = useRef()

  const [form, setForm] = useState({ reviewer_username: '', reviewer_title: '', reviewer_bio: '' })
  const [avatarFile, setAvatarFile] = useState(null)
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  // Always re-populate form from profile whenever profile changes
  useEffect(() => {
    if (!authLoading && (!user || !profile?.is_admin)) { navigate('/'); return }
    if (profile) {
      setForm({
        reviewer_username: profile.reviewer_username || '',
        reviewer_title: profile.reviewer_title || '',
        reviewer_bio: profile.reviewer_bio || '',
      })
      setAvatarPreview(profile.reviewer_avatar_url || null)
    }
  }, [profile, authLoading, user, navigate])

  function handleAvatarChange(e) {
    const file = e.target.files[0]
    if (!file) return
    setAvatarFile(file)
    setAvatarPreview(URL.createObjectURL(file))
  }

  async function handleSave(e) {
    e.preventDefault()
    setError('')
    setSuccess('')
    if (!form.reviewer_username) { setError('Reviewer username is required.'); return }
    if (!/^[a-z0-9_]{3,30}$/.test(form.reviewer_username)) {
      setError('Username must be 3–30 chars, lowercase letters, numbers, underscores only.')
      return
    }
    setSaving(true)
    try {
      let reviewerAvatarUrl = profile?.reviewer_avatar_url || null
      if (avatarFile) {
        reviewerAvatarUrl = await uploadToCloudinary(avatarFile, 'reviewer-avatars')
      }
      const { error: err } = await supabase.from('profiles').update({
        reviewer_username: form.reviewer_username.toLowerCase(),
        reviewer_title: form.reviewer_title,
        reviewer_bio: form.reviewer_bio,
        reviewer_avatar_url: reviewerAvatarUrl,
      }).eq('id', user.id)
      if (err) throw err
      // Refresh profile so form re-populates with saved values
      await fetchProfile(user.id)
      setAvatarFile(null)
      setSuccess('Reviewer profile saved!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.message)
    }
    setSaving(false)
  }

  if (authLoading) return <div style={{ textAlign: 'center', padding: 80 }}><span className="loading-spinner" /></div>
  if (!profile?.is_admin) return null

  const displayName = profile?.display_name || profile?.full_name || 'Reviewer'

  return (
    <div className="admin-settings-page page-fade">
      <div className="container">
        <div className="admin-settings-header">
          <div>
            <Link to="/admin" className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}>← Admin panel</Link>
            <h1>Reviewer profile</h1>
            <p>This profile appears on certificates you approve and on your public reviewer page.</p>
          </div>
          {form.reviewer_username && (
            <Link to={`/reviewer/${form.reviewer_username}`} className="btn btn-secondary btn-sm" target="_blank" rel="noopener noreferrer">
              View public profile →
            </Link>
          )}
        </div>

        <div className="admin-settings-layout">
          <form className="admin-settings-main" onSubmit={handleSave}>

            <div className="card settings-section">
              <h3>Reviewer photo</h3>
              <p className="hint" style={{ marginBottom: 16 }}>Shown on your reviewer profile and on certificates.</p>
              <div className="avatar-upload-row">
                <div className="avatar-upload-area" onClick={() => avatarRef.current.click()}>
                  {avatarPreview
                    ? <img src={avatarPreview} alt="Reviewer" className="avatar-preview-img" />
                    : <div className="avatar-placeholder">{displayName.charAt(0).toUpperCase()}</div>
                  }
                  <div className="avatar-overlay">✏️</div>
                </div>
                <div className="avatar-upload-info">
                  <p style={{ fontSize: 14, color: 'var(--text-2)' }}>Click to upload a professional photo.</p>
                  {avatarPreview && (
                    <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }}
                      onClick={() => { setAvatarPreview(null); setAvatarFile(null) }}>
                      Remove photo
                    </button>
                  )}
                </div>
              </div>
              <input ref={avatarRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
            </div>

            <div className="card settings-section">
              <h3>Reviewer identity</h3>
              <div className="form-group">
                <label>Reviewer username *</label>
                <div className="username-input-wrap">
                  <span className="username-at">@</span>
                  <input
                    type="text"
                    value={form.reviewer_username}
                    onChange={e => setForm(f => ({ ...f, reviewer_username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                    placeholder="yourname"
                    maxLength={30}
                  />
                </div>
                <span className="hint">humanorigin.app/reviewer/{form.reviewer_username || 'yourname'}</span>
              </div>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={form.reviewer_title}
                  onChange={e => setForm(f => ({ ...f, reviewer_title: e.target.value }))}
                  placeholder="e.g. Visual Arts Reviewer, Senior Reviewer..."
                  maxLength={80}
                />
                <span className="hint">Shown under your name on certificates and your reviewer profile.</span>
              </div>
              <div className="form-group">
                <label>Bio</label>
                <textarea
                  rows={4}
                  value={form.reviewer_bio}
                  onChange={e => setForm(f => ({ ...f, reviewer_bio: e.target.value }))}
                  placeholder="Tell creators about your background and expertise..."
                  maxLength={400}
                />
                <span className="hint">{form.reviewer_bio.length}/400 characters</span>
              </div>
            </div>

            {error && <div className="error-msg">{error}</div>}
            {success && <div className="success-msg">{success}</div>}

            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <><span className="loading-spinner" /> Saving...</> : 'Save reviewer profile'}
              </button>
              <Link to="/admin" className="btn btn-ghost">Back to admin</Link>
            </div>
          </form>

          <div className="admin-settings-sidebar">
            <div className="card">
              <h4 style={{ marginBottom: 16 }}>Certificate preview</h4>
              <div className="cert-reviewer-preview">
                <div className="crp-label">Reviewed by</div>
                <div className="crp-name">
                  {avatarPreview && <img src={avatarPreview} alt="" className="crp-avatar" />}
                  <span>{displayName}</span>
                </div>
                {form.reviewer_title && <div className="crp-title">{form.reviewer_title}</div>}
                <div className="crp-team">HumanOrigin Review Team</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
